import { DataTableColumn } from '@hwm/ui-lib/ui/components';
import { t } from '@lingui/macro';
import { Typography } from '@material-ui/core';
import {
  PegasusAdvancedTimeHolidayModel,
  PegasusAdvancedTimeModel,
  PegasusFlowStatusesModel,
  PegasusTimeStatusesModel,
} from '@shared/models';
import { max, parseISO } from 'date-fns';
import { isEmpty } from 'lodash';

export const PEGASUS_HEADER_HEIGHT = 32;
export const PEGASUS_CUSTOM_ROW_HEIGHT = 28;
export const PEGASUS_FREE_COL_MIN_WIDTH = 112;
export const PEGASUS_MAX_HEIGHT = 328;

export enum TimeTypeType {
  Disabled = 'Disabled',
  EveryDay = 'EveryDay',
  Weekday = 'Weekday',
  Weekend = 'Weekend',
  Monday = 'Monday',
  Tuesday = 'Tuesday',
  Wednesday = 'Wednesday',
  Thursday = 'Thursday',
  Friday = 'Friday',
  Saturday = 'Saturday',
  Sunday = 'Sunday',
}

export const TimeTypeOptionsFn = (): Record<TimeTypeType, string> => ({
  [TimeTypeType.Disabled]: t`Disabled`,
  [TimeTypeType.EveryDay]: t`EveryDay`,
  [TimeTypeType.Weekday]: t`Weekday`,
  [TimeTypeType.Weekend]: t`Weekend`,
  [TimeTypeType.Monday]: t`Monday`,
  [TimeTypeType.Tuesday]: t`Tuesday`,
  [TimeTypeType.Wednesday]: t`Wednesday`,
  [TimeTypeType.Thursday]: t`Thursday`,
  [TimeTypeType.Friday]: t`Friday`,
  [TimeTypeType.Saturday]: t`Saturday`,
  [TimeTypeType.Sunday]: t`Sunday`,
});

export enum SentinelPressureTypes {
  Default = '',
  Low = '0',
  High = '1',
}

export const SentinelPressureOptionsFn = (): Record<SentinelPressureTypes, string> => ({
  [SentinelPressureTypes.Default]: '',
  [SentinelPressureTypes.Low]: t`Low`,
  [SentinelPressureTypes.High]: t`High`,
});

export const pegasusAdvancedTimeTableColumns = (
  pressUnit: string,
  editMode?: boolean,
  isSentinel2?: boolean
): DataTableColumn<PegasusAdvancedTimeModel | PegasusAdvancedTimeHolidayModel>[] => [
  {
    Header: t({ message: 'Time', id: '[PegasusTable.Time]' }),
    accessor: 'time',
    width: editMode ? 124 : 68,
  },
  {
    Header:
      t({ message: `Pressure`, id: '[PegasusTable.Pressure]' }) + `${isSentinel2 ? ' [High/Low]' : ` [${pressUnit}]`}`,
    accessor: 'uiPressure',
  },
];

export const pegasusTimeTableColumns = (
  pressUnit: string,
  editMode?: boolean,
  isSentinel2?: boolean
): DataTableColumn<PegasusTimeStatusesModel>[] => [
  {
    Header: t({ message: 'Time Type', id: '[PegasusTable.TimeType]' }),
    accessor: 'timeType',
    width: editMode ? 124 : 112,
    renderCell: ({ data, dataTestId }) => (
      <Typography variant="body2" data-testid={`${dataTestId}-text`}>
        {TimeTypeOptionsFn()[data.timeType] ?? ''}
      </Typography>
    ),
  },
  {
    Header: t({ message: 'Time', id: '[PegasusTable.Time]' }),
    accessor: 'time',
    width: editMode ? 124 : 68,
  },
  {
    Header:
      t({ message: `Pressure`, id: '[PegasusTable.Pressure]' }) + `${isSentinel2 ? ' [High/Low]' : ` [${pressUnit}]`}`,
    accessor: 'uiPressure',
  },
];

export const pegasusFlowTableColumns = (
  flowUnit: string,
  pressUnit: string
): DataTableColumn<PegasusFlowStatusesModel>[] => [
  {
    Header: t({ message: `Flow`, id: '[PegasusTable.Flow]' }) + ` [${flowUnit}]`,
    accessor: 'uiFlow',
    width: 132,
  },
  {
    Header: t({ message: `Pressure`, id: '[PegasusTable.Pressure]' }) + ` [${pressUnit}]`,
    accessor: 'uiPressure',
  },
];

export const formatLatestDateReceived = (
  pegasusStatuses: (PegasusTimeStatusesModel | PegasusFlowStatusesModel)[],
  formatDate: (date: string | Date, dateFormat?: string) => string
) => {
  if (!pegasusStatuses || isEmpty(pegasusStatuses)) {
    return null;
  }

  const maxDate = max(pegasusStatuses?.map((el) => parseISO(el.lastDateReceived)));

  return formatDate(maxDate, 'P p');
};

export const getHours = (time: string) => {
  return parseInt(time.split(':')[0]);
};

export const getMinutes = (time: string) => {
  return parseInt(time.split(':')[1]);
};
